import { isEqual } from 'lodash-es';

import { _isGroup, _isOption } from './use-select-options.js';
import {
  SelectMultiValue,
  SelectSingleValue,
  BaseOption,
  OptionOrGroup,
} from '../types.js';

type _extractSelectedOptionsReturn<OptionValue, Multiple> =
  Multiple extends true
    ? SelectMultiValue<BaseOption<OptionValue>>
    : SelectSingleValue<BaseOption<OptionValue>>;

// Defining a specific return type for the function in order to be able to cast
// the return with a generic in hand.
// https://stackoverflow.com/questions/70553130/typescript-generic-conditional-type-as-return-value-for-generic-function
export function _extractSelectedOptions<OptionValue, Multiple>(
  value: SelectSingleValue<OptionValue> | SelectMultiValue<OptionValue>,
  options: OptionOrGroup<BaseOption<OptionValue>>,
  multiple: Multiple,
): _extractSelectedOptionsReturn<OptionValue, Multiple> {
  // Extraction for single select value.
  if (multiple === false) {
    // Iterate through the options to check the value equality
    for (const option of options) {
      if (_isOption<OptionValue>(option) && isEqual(option.value, value)) {
        return option as _extractSelectedOptionsReturn<OptionValue, Multiple>;
      }

      if (_isGroup<BaseOption<OptionValue>>(option)) {
        // For groups, iterate through the options within the group and return
        // any selected value
        for (const nestedOption of option.options) {
          if (
            _isOption<OptionValue>(nestedOption) &&
            isEqual(nestedOption.value, value)
          ) {
            return nestedOption as _extractSelectedOptionsReturn<
              OptionValue,
              Multiple
            >;
          }
        }
      }
    }
  }

  if (multiple === true && Array.isArray(value)) {
    const selectedOptions: SelectMultiValue<BaseOption<OptionValue>> = [];
    // Iterate through the options to check the value equality
    for (const option of options) {
      if (_isOption<OptionValue>(option)) {
        for (const [index, partialValue] of value.entries()) {
          if (isEqual(option.value, partialValue)) {
            // Assign the selected options to the actual position in the selection value array
            // in order to retain the correct user selection order.
            selectedOptions[index] = option;
          }
        }
      }

      if (_isGroup<BaseOption<OptionValue>>(option)) {
        // For groups, iterate through the options within the group and return
        // any selected value
        for (const nestedOption of option.options) {
          if (_isOption<OptionValue>(nestedOption)) {
            for (const [index, partialValue] of value.entries()) {
              if (isEqual(nestedOption.value, partialValue)) {
                // Assign the selected options to the actual position in the selection value array
                // in order to retain the correct user selection order.
                selectedOptions[index] = nestedOption;
              }
            }
          }
        }
      }
    }

    if (selectedOptions.length > 0) {
      return selectedOptions as _extractSelectedOptionsReturn<
        OptionValue,
        Multiple
      >;
    }
  }

  if (multiple === true) {
    return (<OptionValue>[]) as _extractSelectedOptionsReturn<
      OptionValue,
      Multiple
    >;
  } else {
    return null as _extractSelectedOptionsReturn<OptionValue, Multiple>;
  }
}

// Defining a specific return type for the function in order to be able to cast
// the return with a generic in hand.
// https://stackoverflow.com/questions/70553130/typescript-generic-conditional-type-as-return-value-for-generic-function
type useSelectValueReturn<OptionValue, Multiple> = {
  value?: Multiple extends true
    ? SelectMultiValue<BaseOption<OptionValue>>
    : SelectSingleValue<BaseOption<OptionValue>>;
  defaultValue: Multiple extends true
    ? SelectMultiValue<BaseOption<OptionValue>>
    : SelectSingleValue<BaseOption<OptionValue>>;
};
/**
 * Hook that will provide a proper controlled/uncontrolled value for the react-select.
 * It will handle the controlled / uncontrolled changes and will also selected options
 * from the provided ones.
 */
export function useSelectValue<OptionValue, Multiple extends boolean>({
  value,
  defaultValue,
  options,
  multiple,
}: {
  value?: SelectSingleValue<OptionValue> | SelectMultiValue<OptionValue>;
  defaultValue?: SelectSingleValue<OptionValue> | SelectMultiValue<OptionValue>;
  options: OptionOrGroup<BaseOption<OptionValue>>;
  multiple: Multiple;
}): useSelectValueReturn<OptionValue, Multiple> {
  let controlledValues:
    | SelectMultiValue<BaseOption<OptionValue>>
    | SelectSingleValue<BaseOption<OptionValue>>
    | undefined = undefined;
  let uncontrolledValues:
    | SelectMultiValue<BaseOption<OptionValue>>
    | SelectSingleValue<BaseOption<OptionValue>>
    | undefined = undefined;

  if (value !== undefined) {
    controlledValues = _extractSelectedOptions<OptionValue, Multiple>(
      value,
      options,
      multiple,
    );
  } else if (defaultValue !== undefined) {
    uncontrolledValues =
      defaultValue !== undefined
        ? _extractSelectedOptions<OptionValue, Multiple>(
            defaultValue,
            options,
            multiple,
          )
        : undefined;
  }

  return {
    value: controlledValues,
    defaultValue: uncontrolledValues,
  } as useSelectValueReturn<OptionValue, Multiple>;
}
